/**
 * ImageDropUpload
 * ─────────────────────────────────────────────────────────────
 * A drag-and-drop modal that previews the selected image before
 * calling onPick(File). Drop or click to select, then confirm.
 *
 * Props
 *   variant   "cover" | "hero" | "content"  (default "cover")
 *   multiple  boolean  (default false) — only used for hero
 *   busy      boolean  — show spinner on confirm button
 *   onPick(file|file[])  — called once user confirms
 *   onClose()            — close without picking
 * ─────────────────────────────────────────────────────────────
 * Aspect-ratio guidelines enforced by this component:
 *   cover   → 16:9  recommended 1600 × 900 px
 *   hero    → 16:9  recommended 1600 × 900 px  (multi-select ok)
 *   content → 4:3   recommended 1200 × 900 px
 */

import React, { useCallback, useRef, useState } from "react";
import AdminPortal from "./AdminPortal";
import { ImagePlus, Upload, X, Check, Trash2, Loader2 } from "lucide-react";

// ─── Spec table ──────────────────────────────────────────────
const SPECS = {
  cover: {
    label: "Ảnh bìa (Cover)",
    ratio: "16 : 9",
    hint: "1600 × 900 px trở lên · JPEG / PNG / WebP · ≤ 8 MB",
    accept: "image/jpeg,image/png,image/webp",
    maxMB: 8,
    multiple: false,
    aspectClass: "aspect-video",        // 16:9 preview box
  },
  hero: {
    label: "Ảnh Slider (Hero)",
    ratio: "16 : 9",
    hint: "1600 × 900 px trở lên · JPEG / PNG / WebP · ≤ 8 MB mỗi ảnh",
    accept: "image/jpeg,image/png,image/webp",
    maxMB: 8,
    multiple: true,
    aspectClass: "aspect-video",
  },
  content: {
    label: "Ảnh Nội dung",
    ratio: "4 : 3",
    hint: "1200 × 900 px trở lên · JPEG / PNG / WebP · ≤ 8 MB",
    accept: "image/jpeg,image/png,image/webp",
    maxMB: 8,
    multiple: false,
    aspectClass: "aspect-[4/3]",
  },
};

// ─── Helpers ─────────────────────────────────────────────────
function cls(...parts) {
  return parts.filter(Boolean).join(" ");
}
function createPreview(file) {
  return URL.createObjectURL(file);
}
function fmtSize(bytes) {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}
function isTooBig(file, maxMB) {
  return file.size > maxMB * 1024 * 1024;
}

// ─── Single-file drop zone ────────────────────────────────────
function DropZone({ spec, file, onFile, onRemove }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const handle = useCallback((f) => {
    if (!f) return;
    onFile(f);
  }, [onFile]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    const f = e.dataTransfer.files?.[0];
    if (f) handle(f);
  };

  const preview = file ? createPreview(file) : null;
  React.useEffect(() => {
    return () => { if (preview) URL.revokeObjectURL(preview); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  if (file) {
    const tooBig = isTooBig(file, spec.maxMB);
    return (
      <div className="space-y-3">
        <div className={cls("relative overflow-hidden rounded-2xl border-2 bg-black/5", tooBig ? "border-rose-400" : "border-admin-primary/30", spec.aspectClass)}>
          <img src={preview} alt="preview" className="h-full w-full object-cover" />
          <button
            type="button"
            onClick={onRemove}
            className="absolute right-2 top-2 rounded-full bg-black/50 p-1.5 text-white backdrop-blur transition hover:bg-black/70"
          >
            <Trash2 size={14} />
          </button>
          {tooBig && (
            <div className="absolute inset-x-0 bottom-0 bg-rose-600/80 px-3 py-1 text-center text-xs font-bold text-white">
              Ảnh quá lớn ({fmtSize(file.size)}) — tối đa {spec.maxMB} MB
            </div>
          )}
        </div>
        <div className="rounded-xl border border-admin-border bg-admin-muted/40 px-3 py-2 text-xs text-admin-textMuted">
          <span className="font-semibold text-admin-text">{file.name}</span>{" · "}{fmtSize(file.size)}
        </div>
      </div>
    );
  }

  return (
    <div
      className={cls(
        "relative flex cursor-pointer flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed transition",
        spec.aspectClass,
        dragging
          ? "border-admin-primary bg-admin-primary/10"
          : "border-admin-border bg-admin-muted/35 hover:border-admin-primary hover:bg-admin-muted/60"
      )}
      onClick={() => inputRef.current?.click()}
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={onDrop}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") inputRef.current?.click(); }}
      aria-label="Kéo thả hoặc bấm để chọn ảnh"
    >
      <ImagePlus size={32} className="text-admin-textMuted" />
      <div className="text-center">
        <p className="text-sm font-extrabold text-admin-text">Kéo ảnh vào đây</p>
        <p className="text-xs text-admin-textMuted">hoặc bấm để chọn file</p>
      </div>
      <input
        ref={inputRef}
        type="file"
        accept={spec.accept}
        className="hidden"
        onChange={(e) => { const f = e.target.files?.[0]; if (f) handle(f); e.target.value = ""; }}
      />
    </div>
  );
}

// ─── Multi-file drop zone (hero) ──────────────────────────────
function MultiDropZone({ spec, files, onFiles, onRemove }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const handle = useCallback((fileList) => {
    const arr = Array.from(fileList || []).filter((f) => f.type.startsWith("image/"));
    if (arr.length) onFiles(arr);
  }, [onFiles]);

  const onDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    handle(e.dataTransfer.files);
  };

  return (
    <div className="space-y-3">
      <div
        className={cls(
          "flex cursor-pointer flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed px-6 py-8 transition",
          dragging
            ? "border-admin-primary bg-admin-primary/10"
            : "border-admin-border bg-admin-muted/35 hover:border-admin-primary hover:bg-admin-muted/60"
        )}
        onClick={() => inputRef.current?.click()}
        onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
        onDragLeave={() => setDragging(false)}
        onDrop={onDrop}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => { if (e.key === "Enter" || e.key === " ") inputRef.current?.click(); }}
        aria-label="Kéo ảnh vào đây hoặc bấm để chọn"
      >
        <Upload size={28} className="text-admin-textMuted" />
        <div className="text-center">
          <p className="text-sm font-extrabold text-admin-text">Kéo nhiều ảnh vào đây</p>
          <p className="text-xs text-admin-textMuted">hoặc bấm để chọn nhiều file</p>
        </div>
        <input
          ref={inputRef}
          type="file"
          accept={spec.accept}
          multiple
          className="hidden"
          onChange={(e) => { handle(e.target.files); e.target.value = ""; }}
        />
      </div>

      {files.length > 0 && (
        <div className="grid grid-cols-3 gap-2 sm:grid-cols-4">
          {files.map((f, i) => {
            const url = createPreview(f);
            const tooBig = isTooBig(f, spec.maxMB);
            return (
              <div key={i} className={cls("group relative overflow-hidden rounded-xl border-2", tooBig ? "border-rose-400" : "border-admin-border")}>
                <div className="aspect-video">
                  <img src={url} alt="" className="h-full w-full object-cover" onLoad={() => URL.revokeObjectURL(url)} />
                </div>
                <button
                  type="button"
                  onClick={() => onRemove(i)}
                  className="absolute right-1 top-1 rounded-full bg-black/60 p-1 text-white opacity-0 transition group-hover:opacity-100"
                >
                  <X size={11} />
                </button>
                {tooBig && (
                  <div className="absolute inset-x-0 bottom-0 bg-rose-600/80 px-1 py-0.5 text-center text-[10px] font-bold text-white">
                    Quá {spec.maxMB}MB
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// ─── Main export ──────────────────────────────────────────────
export default function ImageDropUpload({ variant = "cover", busy = false, onPick, onClose }) {
  const spec = SPECS[variant] || SPECS.cover;
  const [file, setFile] = useState(null);        // single
  const [files, setFiles] = useState([]);         // multi

  const canConfirm = spec.multiple
    ? files.length > 0 && files.every((f) => !isTooBig(f, spec.maxMB))
    : file && !isTooBig(file, spec.maxMB);

  const handleConfirm = () => {
    if (!canConfirm) return;
    if (spec.multiple) {
      onPick?.(files);
    } else {
      onPick?.(file);
    }
  };

  return (
    <AdminPortal>
      <div
        className="fixed inset-0 z-[300] flex items-center justify-center bg-black/60 p-4 backdrop-blur-sm"
        role="dialog"
        aria-modal="true"
        aria-label={spec.label}
        onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      >
        <div
          className="flex w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-admin-border bg-admin-surface shadow-soft-xl"
          style={{ maxHeight: "min(90vh, 680px)" }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="flex shrink-0 items-center justify-between gap-3 border-b border-admin-border px-5 py-4">
            <div className="flex items-center gap-3">
              <div className="flex h-9 w-9 items-center justify-center rounded-xl border border-admin-border bg-admin-muted text-admin-primary shadow-sm">
                <ImagePlus size={17} />
              </div>
              <div>
                <p className="text-sm font-extrabold text-admin-text">{spec.label}</p>
                <p className="text-[11px] text-admin-textMuted">Tỉ lệ {spec.ratio}</p>
              </div>
            </div>
            <button
              type="button"
              onClick={onClose}
              className="flex h-8 w-8 items-center justify-center rounded-xl text-admin-textMuted transition hover:bg-admin-muted hover:text-admin-text"
            >
              <X size={16} />
            </button>
          </div>

          {/* Body */}
          <div className="flex-1 overflow-y-auto px-5 py-4">
            {/* Spec badge */}
            <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-admin-border bg-admin-muted/40 px-3 py-2 text-[11px] text-admin-textMuted">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-admin-border bg-admin-surface px-2.5 py-1 font-extrabold text-admin-text shadow-sm">
                Tỉ lệ {spec.ratio}
              </span>
              <span>{spec.hint}</span>
            </div>

            {/* Drop zone */}
            {spec.multiple ? (
              <MultiDropZone
                spec={spec}
                files={files}
                onFiles={(arr) => setFiles((prev) => [...prev, ...arr])}
                onRemove={(i) => setFiles((prev) => prev.filter((_, idx) => idx !== i))}
              />
            ) : (
              <DropZone
                spec={spec}
                file={file}
                onFile={setFile}
                onRemove={() => setFile(null)}
              />
            )}
          </div>

          {/* Footer */}
          <div className="flex shrink-0 items-center justify-end gap-3 border-t border-admin-border bg-admin-surface/90 px-5 py-4">
            <button
              type="button"
              onClick={onClose}
              className="rounded-xl border border-admin-border bg-admin-surface px-4 py-2.5 text-sm font-extrabold text-admin-text shadow-sm transition hover:bg-admin-muted"
            >
              Huỷ
            </button>
            <button
              type="button"
              disabled={!canConfirm || busy}
              onClick={handleConfirm}
              className={cls(
                "inline-flex items-center gap-2 rounded-xl px-5 py-2.5 text-sm font-extrabold shadow-sm transition",
                canConfirm && !busy
                  ? "bg-admin-primary text-white hover:bg-admin-primaryHover"
                  : "cursor-not-allowed bg-admin-muted text-admin-textMuted"
              )}
            >
              {busy ? <Loader2 size={15} className="animate-spin" /> : <Check size={15} />}
              {busy ? "Đang upload..." : "Xác nhận upload"}
            </button>
          </div>
        </div>
      </div>
    </AdminPortal>
  );
}
